import type { AssetSessionPrices, AssetSessionQuote } from '@einhaus/project314-types';
import { getDateFromUnix } from '@einhaus/meteoric-helpers';

export const formatSessionMoney = (value: number, currency: string, signed = false) =>
    value.toLocaleString('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        signDisplay: signed ? 'exceptZero' : 'auto'
    });

export const sessionChangeColor = (value: number | null) =>
    value === null || value === 0 ? 'text-zinc-400' : value > 0 ? 'text-profit-green' : 'text-red-400';

function QuoteLine({
    quote,
    currency,
    label,
    sinceClose = false
}: {
    quote: AssetSessionQuote | null;
    currency: string;
    label: string;
    sinceClose?: boolean;
}) {
    return (
        <div className="space-y-1">
            <div className="text-xs font-medium text-zinc-400">{label}</div>
            {quote ? (
                <>
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 tabular-nums">
                        <span className="text-lg font-semibold text-zinc-100">{formatSessionMoney(quote.price, currency)}</span>
                        {quote.change !== null && quote.changePercent !== null ? (
                            <span className={`text-sm ${sessionChangeColor(quote.change)}`}>
                                {formatSessionMoney(quote.change, currency, true)} (
                                {quote.changePercent.toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                    signDisplay: 'exceptZero'
                                })}
                                %)
                            </span>
                        ) : (
                            <span className="text-xs text-zinc-500">Change unavailable</span>
                        )}
                    </div>
                    <div className="text-xs text-zinc-500">
                        {sinceClose && <span>Since regular close · </span>}
                        <span>{getDateFromUnix(quote.timestamp, 'ymdhms').slice(0, 16)} ET</span>
                        {quote.isStale && <span className="ml-1 text-amber-400">· Stale</span>}
                    </div>
                </>
            ) : (
                <div className="text-sm text-zinc-500">Price unavailable</div>
            )}
        </div>
    );
}

interface AssetSessionPriceDisplayProps {
    prices: AssetSessionPrices | undefined;
    isLoading?: boolean;
    isError?: boolean;
    extendedOnly?: boolean;
}

export default function AssetSessionPriceDisplay({
    prices,
    isLoading = false,
    isError = false,
    extendedOnly = false
}: AssetSessionPriceDisplayProps) {
    if (isError)
        return (
            <div className="text-sm text-amber-400" role="status">
                Prices temporarily unavailable
            </div>
        );
    if (!prices) return <div className="text-sm text-zinc-500">{isLoading ? 'Loading prices…' : 'Prices unavailable'}</div>;

    if (extendedOnly && !prices.extendedSession) return <span className="text-xs text-zinc-500">Regular session</span>;

    return (
        <div
            className="space-y-3 text-left"
            title="One-minute prices, adjusted for stock splits. The regular close uses the final minute of the regular session."
        >
            {!extendedOnly && (
                <QuoteLine
                    currency={prices.currency}
                    label={prices.marketSession === 'regular' ? 'Regular session' : `Regular close · ${prices.regularTradingDate}`}
                    quote={prices.regular}
                />
            )}
            {prices.extendedSession && (
                <QuoteLine
                    sinceClose
                    currency={prices.currency}
                    label={prices.extendedSession === 'pre-market' ? 'Pre-market' : 'After-hours'}
                    quote={prices.extended}
                />
            )}
            {prices.marketSession === 'closed' && <div className="text-xs text-zinc-500">Market closed · Last available session</div>}
        </div>
    );
}
